import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Building2, Check, Edit, Plus, Search, ChevronRight, ArrowLeft } from 'lucide-react';
import WhiteLabelAdmin from './WhiteLabelAdmin';

/**
 * ProfileSelectorModal - Modal to select and manage white-label profiles
 * Opens embedded WhiteLabelAdmin component for editing
 */
const ProfileSelectorModal = ({
  isOpen,
  onClose,
  currentProfile,
  availableProfiles = [],
  onProfileChange,
  darkMode = false,
  primaryColor = '#DC2626'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProfiles, setFilteredProfiles] = useState(availableProfiles);
  const [editingProfile, setEditingProfile] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'edit' | 'create'

  // Reset view when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setView('list');
      setEditingProfile(null);
      setSearchQuery('');
      // Re-sync filtered profiles when modal opens
      setFilteredProfiles(availableProfiles);
    }
  }, [isOpen, availableProfiles]);

  // Filter profiles based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProfiles(availableProfiles);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredProfiles(
        availableProfiles.filter(
          (profile) =>
            (profile.company_name || profile.companyName || '').toLowerCase().includes(query) ||
            (profile.tenant_code || profile.tenantCode || '').toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, availableProfiles]);

  // Handle profile selection
  const handleSelectProfile = (profile) => {
    console.log('[ProfileSelectorModal] handleSelectProfile called with profile:', profile);
    const tenantCode = profile.tenant_code || profile.tenantCode;
    console.log('[ProfileSelectorModal] Extracted tenantCode:', tenantCode);
    onProfileChange(tenantCode);
    onClose();
  };

  // Handle edit profile
  const handleEditProfile = (profile, e) => {
    if (e) e.stopPropagation();
    setEditingProfile(profile);
    setView('edit');
  };

  // Handle create new profile
  const handleCreateNew = () => {
    setEditingProfile(null);
    setView('create');
  };

  // Handle back to list
  const handleBackToList = () => {
    setView('list');
    setEditingProfile(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={view === 'list' ? onClose : undefined}
      />

      {/* Modal */}
      <div
        className={`relative w-full mx-4 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          view === 'list' ? 'max-w-lg' : 'max-w-5xl max-h-[90vh]'
        } ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
        >
          <div className="flex items-center gap-3">
            {view !== 'list' && (
              <button
                onClick={handleBackToList}
                className={`p-2 rounded-lg transition-colors mr-2 ${
                  darkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Building2 size={20} style={{ color: primaryColor }} />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {view === 'list' && 'White-Label Profiles'}
                {view === 'edit' && `Edit: ${editingProfile?.company_name || editingProfile?.companyName || 'Profile'}`}
                {view === 'create' && 'Create New Profile'}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {view === 'list' && 'Select or manage your branding profiles'}
                {view === 'edit' && 'Update branding settings for this profile'}
                {view === 'create' && 'Set up a new white-label configuration'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        {view === 'list' && (
          <>
            {/* Search */}
            <div className="px-6 py-3 border-b" style={{ borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                darkMode ? 'bg-white/5' : 'bg-gray-100'
              }`}>
                <Search size={18} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                <input
                  type="text"
                  placeholder="Search profiles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`flex-1 bg-transparent border-none outline-none text-sm ${
                    darkMode ? 'text-white placeholder:text-gray-500' : 'text-gray-900 placeholder:text-gray-400'
                  }`}
                />
              </div>
            </div>

            {/* Profile List */}
            <div className="max-h-80 overflow-y-auto">
              {/* Default / No White Label Option */}
              <div
                onClick={() => handleSelectProfile({ tenant_code: 'none', company_name: 'Default (No White Label)' })}
                className={`px-6 py-4 flex items-center gap-4 cursor-pointer transition-colors border-b ${
                  (!currentProfile?.tenant_code && !currentProfile?.tenantCode) ||
                  currentProfile?.tenant_code === 'none' || currentProfile?.tenantCode === 'none'
                    ? darkMode
                      ? 'bg-white/10'
                      : 'bg-blue-50'
                    : darkMode
                      ? 'hover:bg-white/5'
                      : 'hover:bg-gray-50'
                }`}
                style={{ borderColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)' }}
                >
                  H
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Default (No White Label)
                    </p>
                    {((!currentProfile?.tenant_code && !currentProfile?.tenantCode) ||
                      currentProfile?.tenant_code === 'none' || currentProfile?.tenantCode === 'none') && (
                      <span
                        className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full"
                        style={{ backgroundColor: 'rgba(220, 38, 38, 0.2)', color: '#DC2626' }}
                      >
                        Active
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Use standard HaliTrade / HaliAgent branding
                  </p>
                </div>
                {((!currentProfile?.tenant_code && !currentProfile?.tenantCode) ||
                  currentProfile?.tenant_code === 'none' || currentProfile?.tenantCode === 'none') ? (
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check size={16} className="text-green-500" />
                  </div>
                ) : (
                  <ChevronRight size={18} className={darkMode ? 'text-gray-600' : 'text-gray-300'} />
                )}
              </div>

              {filteredProfiles.length === 0 ? (
                <div className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No profiles found
                </div>
              ) : (
                filteredProfiles.map((profile) => {
                  const tenantCode = profile.tenant_code || profile.tenantCode;
                  const currentCode = currentProfile?.tenant_code || currentProfile?.tenantCode;
                  const isActive = tenantCode === currentCode;
                  const profileColor = profile.primary_color || profile.primaryColor || primaryColor;

                  return (
                    <div
                      key={profile.id || tenantCode}
                      onClick={() => handleSelectProfile(profile)}
                      className={`px-6 py-4 flex items-center gap-4 cursor-pointer transition-colors border-b ${
                        isActive
                          ? darkMode
                            ? 'bg-white/10'
                            : 'bg-blue-50'
                          : darkMode
                            ? 'hover:bg-white/5'
                            : 'hover:bg-gray-50'
                      }`}
                      style={{ borderColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
                    >
                      {/* Profile Avatar */}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                        style={{ backgroundColor: profileColor }}
                      >
                        {(profile.company_name || profile.companyName || 'P')[0].toUpperCase()}
                      </div>

                      {/* Profile Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {profile.company_name || profile.companyName || profile.tenant_code || profile.tenantCode || 'Profile'}
                          </p>
                          {isActive && (
                            <span
                              className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-full"
                              style={{ backgroundColor: `${profileColor}20`, color: profileColor }}
                            >
                              Active
                            </span>
                          )}
                        </div>
                        <p className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {tenantCode}
                        </p>
                        {profile.tagline && (
                          <p className={`text-xs truncate mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {profile.tagline}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => handleEditProfile(profile, e)}
                          className={`p-2 rounded-lg transition-colors ${
                            darkMode
                              ? 'hover:bg-white/10 text-gray-400 hover:text-white'
                              : 'hover:bg-gray-200 text-gray-400 hover:text-gray-700'
                          }`}
                          title="Edit profile"
                        >
                          <Edit size={16} />
                        </button>
                        {isActive ? (
                          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Check size={16} className="text-green-500" />
                          </div>
                        ) : (
                          <ChevronRight size={18} className={darkMode ? 'text-gray-600' : 'text-gray-300'} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div
              className="px-6 py-4 border-t flex items-center justify-between"
              style={{ borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
            >
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {availableProfiles.length} profile{availableProfiles.length !== 1 ? 's' : ''} available
              </p>
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:scale-105"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus size={16} />
                Create New
              </button>
            </div>
          </>
        )}

        {/* Edit/Create View - Embedded WhiteLabelAdmin */}
        {(view === 'edit' || view === 'create') && (
          <div className="max-h-[calc(90vh-80px)] overflow-y-auto">
            <WhiteLabelAdmin
              key={`${editingProfile?.tenant_code || editingProfile?.tenantCode || 'new'}-${Date.now()}`}
              darkMode={darkMode}
              embedded={true}
              initialTenantCode={editingProfile?.tenant_code || editingProfile?.tenantCode}
              onSaveSuccess={() => {
                // Refresh the profile list and go back
                handleBackToList();
                // Trigger a refresh of available profiles
                if (onProfileChange && editingProfile) {
                  onProfileChange(editingProfile.tenant_code || editingProfile.tenantCode);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

ProfileSelectorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentProfile: PropTypes.object,
  availableProfiles: PropTypes.array,
  onProfileChange: PropTypes.func,
  darkMode: PropTypes.bool,
  primaryColor: PropTypes.string
};

export default ProfileSelectorModal;
